import React, { useState, useMemo, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { getChartColor, getCleanOptions } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { getLegendWrapperStyle } from './chartStyleUtils';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { MarketShareTooltipBody } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import { useIsBelowLg, getResponsiveChartMargin } from '../../hooks/useMediaQuery';

const WIDGET_ID = 'market-share';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MAX_VISIBLE_ORIGINS = 5;

// Get top N origins by total volume
const getTopOrigins = (data, n = 5) => {
  const volumeByOrigin = {};
  data.forEach(item => {
    if (!item.origin) return;
    volumeByOrigin[item.origin] = (volumeByOrigin[item.origin] || 0) + (item.volume || 0);
  });

  return Object.entries(volumeByOrigin)
    .sort(([, a], [, b]) => b - a)
    .slice(0, n)
    .map(([origin]) => origin);
};

/**
 * Market Share Area Chart - 100% stacked area showing market share by origin
 *
 * Data comes as raw volume records with: date, year, month, iso_week, origin, volume, type, variety
 * Frontend aggregates by month and calculates market share percentages
 *
 * Grouping logic (matches Volume by Origin):
 * - If ALL origins selected AND more than 5 origins → Show top 5 + "Others"
 * - If user manually selects specific origins → Show exactly what they selected
 */
const MarketShareAreaChart = ({ data = [], origins = [], years = [], commodity = '' }) => {
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Scope widget options by commodity so multi-select defaults don't restore stale subsets across commodities.
  const widgetKey = useMemo(() => `${WIDGET_ID}:${commodity || '__no_commodity__'}`, [commodity]);
  const { saved: savedWidgetOpts, persist, isReady: prefsReady } = useWidgetOptionsPersistence(widgetKey);

  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedType, setSelectedType] = useState('Conventional'); // Default to Conventional (single-select)
  const [selectedVariety, setSelectedVariety] = useState(null);
  const [selectedOrigins, setSelectedOrigins] = useState([]); // User-selected origins
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const initializedRef = useRef(false);
  /** Skip one allOrigins→select-all sync when restoring Firestore origins */
  const skipAllOriginsSyncRef = useRef(false);
  const pendingSavedOriginsRef = useRef(null);
  const skipInitialPersistRef = useRef(true);

  // Reset init guards when commodity changes (new scope should default-select within available options).
  useLayoutEffect(() => {
    initializedRef.current = false;
    skipAllOriginsSyncRef.current = false;
    pendingSavedOriginsRef.current = null;
    skipInitialPersistRef.current = true;
  }, [commodity]);

  // Reset initialized flag when data changes (e.g., commodity switch)
  // NOTE: Must be declared before the initialization effect below (React runs effects in declaration order)
  useEffect(() => {
    initializedRef.current = false;
    pendingSavedOriginsRef.current = null;
    skipInitialPersistRef.current = true;
  }, [data]);

  // Initialize on first data load (after Firestore prefs load when on Analytics)
  useEffect(() => {
    if (!data || data.length === 0 || initializedRef.current) return;
    if (years.length === 0) return;
    if (!prefsReady) return;

    initializedRef.current = true;

    const s = savedWidgetOpts;
    const hasSaved =
      s &&
      (s.selectedYear != null ||
        s.selectedType ||
        s.selectedVariety != null ||
        (Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0));

    let year = years[0];
    if (hasSaved && s.selectedYear != null && years.includes(s.selectedYear)) {
      year = s.selectedYear;
    }

    let type = 'Conventional';
    if (hasSaved && s.selectedType) {
      const types = getCleanOptions(data, 'type', commodity);
      if (types.includes(s.selectedType)) type = s.selectedType;
    }

    const typeFiltered = data.filter((item) => !type || item.type === type);
    const varOpts = getCleanOptions(typeFiltered, 'variety', commodity);
    let variety = null;
    if (hasSaved && s.selectedVariety && varOpts.includes(s.selectedVariety)) {
      variety = s.selectedVariety;
    }

    setSelectedYear(year);
    setSelectedType(type);
    setSelectedVariety(variety);

    if (typeof s?.filtersExpanded === 'boolean') {
      setFiltersExpanded(s.filtersExpanded);
    }

    if (hasSaved && Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0) {
      pendingSavedOriginsRef.current = s.selectedOrigins;
    }
  }, [data, years, prefsReady, savedWidgetOpts, commodity]);

  // Get filter options (excluding N/A values)
  const typeOptions = useMemo(() => getCleanOptions(data, 'type', commodity), [data, commodity]);
  const typeFilteredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    if (!selectedType) return data;
    return data.filter(item => item.type === selectedType);
  }, [data, selectedType]);
  const varietyOptions = useMemo(() => getCleanOptions(typeFilteredData, 'variety', commodity), [typeFilteredData, commodity]);

  useEffect(() => {
    if (selectedVariety && varietyOptions.length > 0 && !varietyOptions.includes(selectedVariety)) {
      setSelectedVariety(null);
    }
  }, [varietyOptions, selectedVariety]);

  // Filter data by type and variety
  const filteredData = useMemo(() => {
    return data.filter(item => {
      // Filter by selected type (single-select)
      if (selectedType && item.type !== selectedType) return false;
      if (selectedVariety && item.variety !== selectedVariety) return false;
      return true;
    });
  }, [data, selectedType, selectedVariety]);

  // Get all unique origins from filtered data for selected year
  const allOrigins = useMemo(() => {
    if (!filteredData || filteredData.length === 0 || selectedYear === null) return [];

    const yearData = filteredData.filter(d => d.year === selectedYear);
    const originSet = new Set();
    yearData.forEach(item => {
      if (item.origin) originSet.add(item.origin);
    });
    return [...originSet].sort();
  }, [filteredData, selectedYear]);

  // Apply persisted origins once allOrigins is known for the restored year/type/variety
  useEffect(() => {
    const pending = pendingSavedOriginsRef.current;
    if (!pending?.length) return;
    if (selectedYear == null) return;
    if (allOrigins.length === 0) {
      const yearData = filteredData.filter((d) => d.year === selectedYear);
      if (yearData.length === 0) {
        pendingSavedOriginsRef.current = null;
      }
      return;
    }
    const valid = pending.filter((o) => allOrigins.includes(o));
    pendingSavedOriginsRef.current = null;
    if (valid.length > 0) {
      skipAllOriginsSyncRef.current = true;
      setSelectedOrigins(valid);
    }
  }, [allOrigins, selectedYear, filteredData]);

  // Re-init origins when allOrigins changes (year/type/variety filter change)
  useEffect(() => {
    if (skipAllOriginsSyncRef.current) {
      skipAllOriginsSyncRef.current = false;
      return;
    }
    if (allOrigins.length > 0) {
      setSelectedOrigins([...allOrigins]);
    } else {
      setSelectedOrigins([]);
    }
  }, [allOrigins]);

  const persistFilters = useCallback(() => {
    if (selectedYear == null) return;
    persist({
      filtersExpanded,
      selectedYear,
      selectedType,
      selectedVariety,
      selectedOrigins,
    });
  }, [persist, filtersExpanded, selectedYear, selectedType, selectedVariety, selectedOrigins]);

  useEffect(() => {
    if (!initializedRef.current || !prefsReady) return;
    if (selectedYear == null) return;
    if (skipInitialPersistRef.current) {
      skipInitialPersistRef.current = false;
      return;
    }
    persistFilters();
  }, [prefsReady, selectedYear, selectedType, selectedVariety, selectedOrigins, filtersExpanded, persistFilters]);

  // Determine which origins to show (top 5 + others if all selected AND > 5)
  const { originsToShow, useTop5Logic } = useMemo(() => {
    if (selectedOrigins.length === 0) {
      return { originsToShow: [], useTop5Logic: false };
    }

    const isAllSelected = selectedOrigins.length === allOrigins.length;
    const yearData = filteredData.filter(d => d.year === selectedYear);

    // Only use top 5 + Others when ALL origins are selected AND there are more than 5
    if (isAllSelected && allOrigins.length > MAX_VISIBLE_ORIGINS) {
      return { originsToShow: getTopOrigins(yearData, MAX_VISIBLE_ORIGINS), useTop5Logic: true };
    }

    // Otherwise show exactly what the user selected
    return { originsToShow: selectedOrigins, useTop5Logic: false };
  }, [selectedOrigins, allOrigins, filteredData, selectedYear]);

  // Aggregate raw volume data and calculate market share
  const chartData = useMemo(() => {
    if (!filteredData || filteredData.length === 0 || selectedYear === null) return [];

    // Filter by selected year
    const yearData = filteredData.filter(d => d.year === selectedYear);
    if (yearData.length === 0) return [];

    // Aggregate volume by month and origin
    const aggregated = {};
    yearData.forEach(item => {
      const month = item.month;
      const origin = item.origin;
      const volume = item.volume || 0;

      if (!origin) return;
      // Only include origins that are selected (or all if using top5 logic)
      if (!useTop5Logic && !selectedOrigins.includes(origin)) return;

      if (!aggregated[month]) {
        aggregated[month] = { origins: {}, total: 0 };
      }
      if (!aggregated[month].origins[origin]) {
        aggregated[month].origins[origin] = 0;
      }
      aggregated[month].origins[origin] += volume;
      aggregated[month].total += volume;
    });

    // Build display keys
    const displayKeys = [...originsToShow];
    if (useTop5Logic) {
      displayKeys.push('Others');
    }

    // Convert to chart data with market share percentages
    const result = [];
    Object.entries(aggregated)
      .sort(([a], [b]) => Number(a) - Number(b))
      .forEach(([month, { origins: monthOrigins, total }]) => {
        const monthLabel = MONTH_LABELS[Number(month) - 1];
        const row = { period: monthLabel, monthNum: Number(month), _rawData: {} };

        // Calculate market share for visible origins
        originsToShow.forEach(origin => {
          const volume = monthOrigins[origin] || 0;
          row[origin] = total > 0 ? volume / total : 0;
          row._rawData[origin] = { volume, share: row[origin] };
        });

        // Calculate "Others" aggregate if using top5 logic
        if (useTop5Logic) {
          let othersVolume = 0;
          const othersBreakdown = {};
          Object.entries(monthOrigins).forEach(([origin, volume]) => {
            if (!originsToShow.includes(origin)) {
              othersVolume += volume;
              othersBreakdown[origin] = { volume, share: total > 0 ? volume / total : 0 };
            }
          });
          row['Others'] = total > 0 ? othersVolume / total : 0;
          row._rawData['Others'] = { volume: othersVolume, share: row['Others'], breakdown: othersBreakdown };
        }

        result.push(row);
      });

    return result;
  }, [filteredData, selectedYear, selectedOrigins, originsToShow, useTop5Logic]);

  // Build display origins list for chart
  const displayOrigins = useMemo(() => {
    if (useTop5Logic) {
      return [...originsToShow, 'Others'];
    }
    return originsToShow;
  }, [originsToShow, useTop5Logic]);

  const chartTooltipBoundaryRef = useRef(null);
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(chartTooltipBoundaryRef);
  const isBelowLg = useIsBelowLg();

  const marketShareTooltipContent = useCallback(
    (props) => <MarketShareTooltipBody {...props} theme={theme} fontSize={fontSize} />,
    [theme, fontSize]
  );

  const handleOriginToggle = (origin) => {
    if (selectedOrigins.includes(origin)) {
      setSelectedOrigins(selectedOrigins.filter(o => o !== origin));
    } else {
      setSelectedOrigins([...selectedOrigins, origin]);
    }
  };

  const getOriginDisplayText = () => {
    if (selectedOrigins.length === 0) return 'Select regions';
    if (selectedOrigins.length === allOrigins.length) return 'All regions';
    return `${selectedOrigins.length} selected`;
  };

  const helpDescription = "Stacked area chart showing how market share is distributed among different origins over time. Track seasonal shifts and identify which regions dominate the market.";

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <div className="border-y border-[#5D625F] py-2 px-4 flex-shrink-0">
          <div className="flex items-center gap-1">
            <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
              Market Share
            </h3>
            <ChartHelpButton title="Market Share" description={helpDescription} />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-400 text-center" style={{ fontSize }}>
            No market share data available.<br />Select a different commodity.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
      <div className="border-b border-[#5D625F] py-2 flex-shrink-0">
        <div className="flex items-center gap-1">
          <h3 className="text-[#5D625F] uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4 }}>
            Market Share - {selectedYear || '...'}
          </h3>
          <ChartHelpButton title="Market Share" description={helpDescription} />
        </div>
      </div>

      </div>

      <div className="flex-1 flex flex-col min-h-0 px-4">
        {/* Filters Header */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between py-2 border-b border-[#3a3c3b] flex-shrink-0"
        >
          <span className="text-gray-400 font-medium" style={{ fontSize: fontSize - 2 }}>Filters</span>
          {filtersExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
        </button>

        {filtersExpanded && (
          <div className="py-2 space-y-2 flex-shrink-0">
            <div className="grid grid-cols-2 gap-2">
              <FilterDropdown
                label="Year"
                value={selectedYear || '-'}
                onClick={() => setYearModalOpen(true)}
              />
              {typeOptions.length > 0 && (
                <FilterDropdown
                  label="Type"
                  value={selectedType || 'Select'}
                  onClick={() => setTypeModalOpen(true)}
                />
              )}
            </div>
            {(varietyOptions.length > 0 || allOrigins.length > 0) && (
              <div className="grid grid-cols-2 gap-2">
                {varietyOptions.length > 0 && (
                  <FilterDropdown
                    label="Variety"
                    value={selectedVariety || 'All varieties'}
                    onClick={() => setVarietyModalOpen(true)}
                  />
                )}
                <FilterDropdown
                  label="Regions"
                  value={getOriginDisplayText()}
                  onClick={() => setOriginModalOpen(true)}
                />
              </div>
            )}
          </div>
        )}

        {/* Chart */}
        <div className="flex-1 min-h-0" ref={chartTooltipBoundaryRef}>
          {chartData.length > 0 && displayOrigins.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData}
                margin={getResponsiveChartMargin({ isBelowLg })}
                stackOffset="expand"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                <XAxis
                  dataKey="period"
                  stroke="#666"
                  tick={{ fontSize: fontSize - 2 }}
                  angle={-45}
                  textAnchor="end"
                  height={28}
                />
                <YAxis
                  stroke="#666"
                  tick={{ fontSize }}
                  domain={[0, 1]}
                  tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
                />
                <Tooltip
                  key={tooltipDismissKey}
                  trigger={tooltipTrigger}
                  throttleDelay={50}
                  content={marketShareTooltipContent}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={getLegendWrapperStyle({ fontSize: fontSize - 2, paddingTop: 0, bottom: 4 })}
                />
                {displayOrigins.map((origin, idx) => (
                  <Area
                    key={origin}
                    type="monotone"
                    dataKey={origin}
                    stackId="1"
                    stroke={origin === 'Others' ? '#666' : getChartColor(idx, displayOrigins.length)}
                    fill={origin === 'Others' ? '#444' : getChartColor(idx, displayOrigins.length)}
                    fillOpacity={origin === 'Others' ? 0.5 : 0.7}
                  />
                ))}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 text-sm">
                {selectedYear === null ? 'Loading...' : selectedOrigins.length === 0 ? 'Select at least one region' : 'No data for selected filters'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Origin selection modal */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title="Select Regions"
        options={allOrigins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginToggle}
        multiSelect
        onSelectAll={() => setSelectedOrigins([...allOrigins])}
        onClearAll={() => setSelectedOrigins([])}
      />

      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title="Select Year"
        options={years.map(String)}
        selectedValues={selectedYear ? String(selectedYear) : ''}
        onSelect={(yearStr) => {
          setSelectedYear(Number(yearStr));
        }}
      />

      {typeOptions.length > 0 && (
        <FilterModal
          isOpen={typeModalOpen}
          onClose={() => setTypeModalOpen(false)}
          title="Select Type"
          options={typeOptions}
          selectedValues={selectedType}
          onSelect={(type) => {
            setSelectedType(type);
            setSelectedVariety(null);
          }}
        />
      )}

      {varietyOptions.length > 0 && (
        <FilterModal
          isOpen={varietyModalOpen}
          onClose={() => setVarietyModalOpen(false)}
          title="Select Variety"
          options={varietyOptions}
          selectedValues={selectedVariety}
          onSelect={(variety) => {
            setSelectedVariety(variety);
          }}
        />
      )}
    </div>
  );
};

export default MarketShareAreaChart;
